import {useContext, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {NavigateWithTracking} from '../NavigateWithTracking';
import {useAccountSignup} from '../../contexts/AccountSignupContext';
import {BILLING_CYCLE, BillingCycle} from '../../constants/plans';
import {FREE_PLAN_NAMES} from '../../../constants/plan';
import {globalUIContext} from '../../utils/global-ui-helpers';
import '../../globals';

const isValidBillingCycle = (value: string): value is BillingCycle =>
  (BILLING_CYCLE as readonly string[]).includes(value);

const isFreePlan = (plan: string): boolean => FREE_PLAN_NAMES.includes(plan);

const SUPPLIER_CLIENT_TYPES = ['hotel', 'venue', 'supplier'] as const;

const mapClientType = (value: string): 'planner' | 'supplier' | null => {
  if (!value) return null;
  if (value === 'planner') return 'planner';
  if ((SUPPLIER_CLIENT_TYPES as readonly string[]).includes(value))
    return 'supplier';
  // Any other known business type (eventPlanner, serviceProvider, host, etc.) maps to planner
  return 'planner';
};

export const SignupEntryGuard = () => {
  const [searchParams] = useSearchParams();
  const {updateFormValues} = useAccountSignup();
  const {setGlobalEmail} = useContext(globalUIContext);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);

  const plan = searchParams.get('plan');
  const billingCycle = searchParams.get('billingCycle');
  const clientType = searchParams.get('clientType');

  useEffect(() => {
    if (clientType) {
      const mapped = mapClientType(clientType);
      if (mapped) {
        updateFormValues({
          clientType: mapped,
          hasAccount: Boolean(window.userEmail),
        });
        setGlobalEmail(window.userEmail ?? null);
        setNavigateTo('plans');
        return;
      }
    }

    if (plan && billingCycle && isValidBillingCycle(billingCycle)) {
      // Paid plan: both plan and valid billing cycle provided
      updateFormValues({
        preSelectedPlan: plan,
        preSelectedBillingCycle: billingCycle,
      });
    } else if (plan && !billingCycle && isFreePlan(plan)) {
      // Free plan: no billing cycle needed
      updateFormValues({
        preSelectedPlan: plan,
        preSelectedBillingCycle: undefined,
      });
    } else {
      updateFormValues({
        preSelectedPlan: undefined,
        preSelectedBillingCycle: undefined,
      });
    }
    setNavigateTo('business-type');
  }, [plan, billingCycle, clientType, updateFormValues, setGlobalEmail]);

  if (!navigateTo) return null;
  return <NavigateWithTracking to={navigateTo} replace />;
};
